import React, { useState, useEffect, useCallback } from 'react';
import {
  Layout, Button, Tooltip, Avatar, Table, Tag,
  message, Select, Space, Spin, Typography
} from 'antd';
import {
  UserOutlined, LogoutOutlined, ReloadOutlined,
  FileTextOutlined, ApartmentOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import TabNavigation from './TabNavigation';
import EnvVersionLabel from './Envversionlabel';
import TraceTimelineModal from './TraceTimelineModal';

const { Header, Sider, Content } = Layout;
const { Option } = Select;
const { Text } = Typography;

// ── HCC Observatory service base URL ─────────────────────────────────────────
const HCC_OBS_BASE_URL = process.env.REACT_APP_HCC_OBSERVATORY_URL || 'http://localhost:9038';

// Window options — must match VALID_WINDOWS in hcc_observatory_service.py
const WINDOW_OPTIONS = [
  { value: 7,  label: 'Last 7 Days'  },
  { value: 15, label: 'Last 15 Days' },
  { value: 30, label: 'Last 30 Days' },
  { value: 60, label: 'Last 60 Days' },
  { value: 90, label: 'Last 90 Days' },
];

/**
 * HCCTracingPage
 * Collapsible batch → chart trace browser for the HCC coding pipeline.
 *
 * Layout:
 *   Outer table: one row per batch run (from /api/hcc-observatory/batches)
 *   Inner table: one row per chart within that batch (lazy-loaded on expand
 *                from /api/hcc-observatory/batch/{batch_id}/charts)
 *   Clicking a chart row opens TraceTimelineModal with the full Gantt +
 *   Span Details drawer — reused unchanged from the generic observatory.
 */
const HCCTracingPage = ({
  username,
  sessionId,
  userRole,
  userFeatures,
  tabOrder,
  onLogout,
}) => {
  const navigate = useNavigate();
  const baseUrl = process.env.REACT_APP_API_BASE_URL || '';

  // ── Tab / feature state ───────────────────────────────────────────────────
  const [features,      setFeatures]      = useState(userFeatures || ['HUB', 'OBSERVATORY']);
  const [localTabOrder, setLocalTabOrder] = useState(tabOrder     || ['HUB', 'RCM', 'COLAB', 'MCP', 'OBSERVATORY']);

  // ── Filter ────────────────────────────────────────────────────────────────
  const [windowDays, setWindowDays] = useState(() =>
    parseInt(localStorage.getItem('hcc_tracing_windowDays') || '30', 10)
  );

  // ── Batch list state ──────────────────────────────────────────────────────
  const [batches,        setBatches]        = useState([]);
  const [batchLoading,   setBatchLoading]   = useState(false);

  // ── Expanded chart data — keyed by batch_id ───────────────────────────────
  // { batch_id: { loading: bool, charts: [], error: string|null } }
  const [expandedCharts, setExpandedCharts] = useState({});

  // ── Trace timeline modal ──────────────────────────────────────────────────
  const [modalVisible,   setModalVisible]   = useState(false);
  const [selectedTrace,  setSelectedTrace]  = useState(null);

  // ── Authenticated fetch helper ────────────────────────────────────────────
  const fetchHcc = useCallback(async (path) => {
    const resp = await fetch(`${HCC_OBS_BASE_URL}${path}`, {
      headers: { Authorization: `Bearer ${sessionId}` },
      credentials: 'include',
    });
    if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
    const json = await resp.json();
    if (!json.success) throw new Error(json.message || 'API error');
    return json.data;
  }, [sessionId]);

  // ── Feature refresh (mirrors other HCC pages) ─────────────────────────────
  useEffect(() => {
    const refreshFeatures = async () => {
      try {
        const resp = await fetch(`${baseUrl}/features`, {
          headers: { Authorization: `Bearer ${sessionId}` },
          credentials: 'include',
        });
        if (resp.ok) {
          const { features: srv, tab_order } = await resp.json();
          setFeatures(srv);
          if (tab_order) setLocalTabOrder(tab_order);
        }
      } catch (err) {
        console.warn('Failed to refresh features:', err);
      }
    };
    if (sessionId && !userFeatures) refreshFeatures();
  }, [sessionId, userFeatures, baseUrl]);

  // ── Persist window selection ──────────────────────────────────────────────
  useEffect(() => {
    localStorage.setItem('hcc_tracing_windowDays', String(windowDays));
  }, [windowDays]);

  // ── Auto-load batches on mount ────────────────────────────────────────────
  useEffect(() => {
    loadBatches(windowDays);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Load batch list ───────────────────────────────────────────────────────
  const loadBatches = async (days) => {
    try {
      setBatchLoading(true);
      setExpandedCharts({});  // clear cached chart data when filter changes
      const data = await fetchHcc(`/api/hcc-observatory/batches?window_days=${days}`);
      setBatches(data.batches || []);
    } catch (err) {
      console.error('loadBatches failed:', err);
      message.error(`Failed to load batches: ${err.message}`);
      setBatches([]);
    } finally {
      setBatchLoading(false);
    }
  };

  // ── Lazy-load charts for a batch (called on row expand) ───────────────────
  const loadChartsForBatch = async (batchId) => {
    // Already loaded or loading — skip
    if (expandedCharts[batchId]) return;

    setExpandedCharts(prev => ({
      ...prev,
      [batchId]: { loading: true, charts: [], error: null },
    }));

    try {
      const data = await fetchHcc(
        `/api/hcc-observatory/batch/${batchId}/charts?window_days=${windowDays}`
      );
      setExpandedCharts(prev => ({
        ...prev,
        [batchId]: { loading: false, charts: data.charts || [], error: null },
      }));
    } catch (err) {
      console.error(`loadChartsForBatch(${batchId}) failed:`, err);
      setExpandedCharts(prev => ({
        ...prev,
        [batchId]: { loading: false, charts: [], error: err.message },
      }));
    }
  };

  // ── Handle row expand ─────────────────────────────────────────────────────
  const handleBatchExpand = (expanded, record) => {
    if (expanded) loadChartsForBatch(record.batch_id);
  };

  // ── Open trace modal from a chart row ────────────────────────────────────
  const handleChartTraceClick = (chart) => {
    setSelectedTrace({
      trace_id:   chart.trace_id,
      agent_name: 'hcc_coding_agent',
      agent_type: chart.chart_type,
    });
    setModalVisible(true);
  };

  // ── Formatters ────────────────────────────────────────────────────────────
  const formatLatency = (ms) => {
    if (ms == null) return 'N/A';
    if (ms < 1000)  return `${ms.toFixed(0)}ms`;
    return `${(ms / 1000).toFixed(2)}s`;
  };

  const formatDateTime = (iso) => {
    if (!iso) return 'N/A';
    return new Date(iso).toLocaleString('en-US', {
      month:   'short',
      day:     '2-digit',
      hour:    '2-digit',
      minute:  '2-digit',
      second:  '2-digit',
      hour12:  false,
    });
  };

  const formatCost = (val) => {
    if (val == null) return '-';
    return `$${Number(val).toFixed(4)}`;
  };

  const statusTag = (status) => {
    const color = status === 'COMPLETED' || status === 'completed'
      ? '#92A372'
      : status === 'FAILED' || status === 'failed'
        ? 'error'
        : 'processing';
    return <Tag color={color}>{(status || 'UNKNOWN').toUpperCase()}</Tag>;
  };

  // ── Outer table: batch columns ────────────────────────────────────────────
  const batchColumns = [
    {
      title:     'Batch ID',
      dataIndex: 'batch_id',
      key:       'batch_id',
      width:     140,
      render: (v) => (
        <Text strong style={{ fontFamily: 'monospace', fontSize: '13px' }}>{v || '—'}</Text>
      ),
    },
    {
      title:     'Run ID',
      dataIndex: 'run_id',
      key:       'run_id',
      width:     160,
      ellipsis:  true,
      render: (v) => (
        <Text style={{ fontFamily: 'monospace', fontSize: '12px', color: '#595959' }}>{v || '—'}</Text>
      ),
    },
    {
      title:     'Start Time',
      dataIndex: 'start_time',
      key:       'start_time',
      width:     175,
      render: (v) => <span style={{ fontSize: '13px' }}>{formatDateTime(v)}</span>,
    },
    {
      title:     'Duration',
      dataIndex: 'latency_ms',
      key:       'latency_ms',
      width:     100,
      align:     'right',
      render: (v) => <span style={{ fontSize: '13px' }}>{formatLatency(v)}</span>,
    },
    {
      title:     'Charts',
      dataIndex: 'n_charts',
      key:       'n_charts',
      width:     72,
      align:     'center',
      render: (v) => (
        <Tag color="blue" style={{ minWidth: 32, textAlign: 'center' }}>{v}</Tag>
      ),
    },
    {
      title:     'LLM Calls',
      dataIndex: 'n_llm_calls',
      key:       'n_llm_calls',
      width:     90,
      align:     'center',
      render: (v) => <span style={{ fontSize: '13px' }}>{v?.toLocaleString()}</span>,
    },
    {
      title:     'Input Tokens',
      dataIndex: 'input_tokens',
      key:       'input_tokens',
      width:     110,
      align:     'right',
      render: (v) => <span style={{ fontSize: '13px', color: '#162349' }}>{v?.toLocaleString()}</span>,
    },
    {
      title:     'Output Tokens',
      dataIndex: 'output_tokens',
      key:       'output_tokens',
      width:     115,
      align:     'right',
      render: (v) => <span style={{ fontSize: '13px', color: '#1c97d4' }}>{v?.toLocaleString()}</span>,
    },
    {
      title:     'Input Cost',
      dataIndex: 'input_cost',
      key:       'input_cost',
      width:     100,
      align:     'right',
      render: (v) => <span style={{ fontSize: '13px', color: '#162349', fontWeight: 500 }}>{formatCost(v)}</span>,
    },
    {
      title:     'Output Cost',
      dataIndex: 'output_cost',
      key:       'output_cost',
      width:     105,
      align:     'right',
      render: (v) => <span style={{ fontSize: '13px', color: '#1c97d4', fontWeight: 500 }}>{formatCost(v)}</span>,
    },
    {
      title:     'Total Cost',
      dataIndex: 'total_cost',
      key:       'total_cost',
      width:     100,
      align:     'right',
      render: (v) => <span style={{ fontSize: '13px', fontWeight: 600 }}>{formatCost(v)}</span>,
    },
    {
      title:  'Status',
      key:    'status',
      width:  110,
      render: (_, r) => statusTag(r.status),
    },
  ];

  // ── Inner table: chart columns ────────────────────────────────────────────
  const chartColumns = [
    {
      title:     'Chart ID',
      dataIndex: 'chart_id',
      key:       'chart_id',
      width:     160,
      render: (v) => (
        <Text style={{ fontFamily: 'monospace', fontSize: '12px' }}>{v}</Text>
      ),
    },
    {
      title:     'Type',
      dataIndex: 'chart_type',
      key:       'chart_type',
      width:     90,
      render: (v) => <Tag color="purple">{v}</Tag>,
    },
    {
      title:     'Encounters',
      dataIndex: 'n_encounters',
      key:       'n_encounters',
      width:     95,
      align:     'center',
      render: (v) => v,
    },
    {
      title:     'Steps',
      dataIndex: 'n_steps',
      key:       'n_steps',
      width:     68,
      align:     'center',
      render: (v) => v,
    },
    {
      title:     'LLM Calls',
      dataIndex: 'n_llm_calls',
      key:       'n_llm_calls',
      width:     85,
      align:     'center',
      render: (v) => v,
    },
    {
      title:     'Wall Clock',
      dataIndex: 'wall_clock_ms',
      key:       'wall_clock_ms',
      width:     100,
      align:     'right',
      render: (v) => formatLatency(v),
    },
    {
      title:     'Start Time',
      dataIndex: 'start_time',
      key:       'start_time',
      width:     165,
      render: (v) => (
        <span style={{ fontSize: '12px' }}>{formatDateTime(v)}</span>
      ),
    },
    {
      title:     'In Tokens',
      dataIndex: 'input_tokens',
      key:       'input_tokens',
      width:     100,
      align:     'right',
      render: (v) => (
        <span style={{ color: '#162349', fontSize: '12px' }}>{v?.toLocaleString()}</span>
      ),
    },
    {
      title:     'Out Tokens',
      dataIndex: 'output_tokens',
      key:       'output_tokens',
      width:     105,
      align:     'right',
      render: (v) => (
        <span style={{ color: '#1c97d4', fontSize: '12px' }}>{v?.toLocaleString()}</span>
      ),
    },
    {
      title:     'In Cost',
      dataIndex: 'input_cost',
      key:       'input_cost',
      width:     88,
      align:     'right',
      render: (v) => (
        <span style={{ color: '#162349', fontWeight: 500, fontSize: '12px' }}>{formatCost(v)}</span>
      ),
    },
    {
      title:     'Out Cost',
      dataIndex: 'output_cost',
      key:       'output_cost',
      width:     88,
      align:     'right',
      render: (v) => (
        <span style={{ color: '#1c97d4', fontWeight: 500, fontSize: '12px' }}>{formatCost(v)}</span>
      ),
    },
    {
      title:     'Total Cost',
      dataIndex: 'total_cost',
      key:       'total_cost',
      width:     92,
      align:     'right',
      render: (v) => (
        <span style={{ fontWeight: 600, fontSize: '12px' }}>{formatCost(v)}</span>
      ),
    },
    {
      title:  'Status',
      key:    'status',
      width:  100,
      render: (_, r) => statusTag(r.status),
    },
    {
      title:  'Trace',
      key:    'action',
      width:  110,
      fixed:  'right',
      render: (_, chart) => (
        <Button
          type="primary"
          size="small"
          icon={<ApartmentOutlined />}
          onClick={() => handleChartTraceClick(chart)}
          style={{ background: '#162349', borderColor: '#162349', fontSize: '12px' }}
        >
          View Trace
        </Button>
      ),
    },
  ];

  // ── expandedRowRender — inner chart table ─────────────────────────────────
  const expandedRowRender = (batchRecord) => {
    const state = expandedCharts[batchRecord.batch_id];

    if (!state || state.loading) {
      return (
        <div style={{ padding: '24px', textAlign: 'center' }}>
          <Spin tip="Loading charts…" />
        </div>
      );
    }

    if (state.error) {
      return (
        <div style={{ padding: '16px', color: '#f5222d', fontSize: '13px' }}>
          Failed to load charts: {state.error}
        </div>
      );
    }

    if (!state.charts.length) {
      return (
        <div style={{ padding: '16px', color: '#8c8c8c', fontSize: '13px' }}>
          No chart records found for this batch.
        </div>
      );
    }

    return (
      <div style={{
        padding:         '0 0 0 48px',
        backgroundColor: '#fafafa',
        borderTop:       '1px solid #e8e8e8',
      }}>
        <div style={{
          padding:    '8px 0 4px',
          fontSize:   '12px',
          color:      '#8c8c8c',
          fontWeight: 500,
        }}>
          {state.charts.length} chart{state.charts.length !== 1 ? 's' : ''} in batch{' '}
          <Text code style={{ fontSize: '11px' }}>{batchRecord.batch_id}</Text>
          {' '} — click <strong>View Trace</strong> to open the full Gantt timeline
        </div>
        <Table
          columns={chartColumns}
          dataSource={state.charts}
          rowKey="chart_id"
          size="small"
          pagination={false}
          scroll={{ x: 1400 }}
          style={{ marginBottom: '8px' }}
          rowClassName={(_, i) => i % 2 === 0 ? '' : 'ant-table-row-light'}
        />
      </div>
    );
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <Layout style={{ minHeight: '100vh' }}>

      {/* ── Sidebar ── */}
      <Sider
        width={60}
        collapsible
        collapsed={true}
        collapsedWidth={60}
        trigger={null}
        className="sidebar-gradient"
        style={{
          display:       'flex',
          background:    'linear-gradient(180deg, #162349 0%, #1c97d4 100%)',
          flexDirection: 'column',
          height:        '100vh',
          position:      'fixed',
          left: 0, top: 0, bottom: 0,
          zIndex: 10,
        }}
      >
        <div style={{
          display:        'flex',
          flexDirection:  'column',
          justifyContent: 'space-between',
          height:         '100%',
          alignItems:     'center',
          padding:        '20px 0',
        }}>
          <div />
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', alignItems: 'center' }}>
            <div id="containerDiv">
              <hr style={{ backgroundColor: 'white', color: 'white', height: '10px', border: 'none' }} />
            </div>
            <Tooltip title={username} placement="right">
              <Avatar
                style={{ backgroundColor: 'white', color: '#162349', cursor: 'pointer', border: '2px solid white' }}
                icon={<UserOutlined />}
              />
            </Tooltip>
            <Tooltip title="Logout" placement="right">
              <Button
                type="text"
                icon={<LogoutOutlined style={{ color: 'white' }} />}
                onClick={onLogout}
                style={{ color: 'white' }}
              />
            </Tooltip>
            <EnvVersionLabel style={{ color: 'rgba(255,255,255,0.6)', fontSize: '12px', fontWeight: 'Bold' }} />
          </div>
        </div>
      </Sider>

      {/* ── Main layout ── */}
      <Layout style={{ marginLeft: '60px' }}>

        {/* ── Header ── */}
        <Header style={{
          background: '#162349',
          padding:    '8px 24px 0 24px',
          height:     'auto',
          color:      'white',
        }}>
          <div style={{ display: 'flex', alignItems: 'flex-end' }}>
            <TabNavigation
              features={features}
              activeTab="TRACE"
              tabOrder={localTabOrder}
            />
          </div>
        </Header>

        {/* ── Content ── */}
        <Content style={{
          padding:    '24px',
          background: 'linear-gradient(to bottom, #f8f9fa 0%, #f0f2f5 33%, #ced4da 90%, #ced4da 100%)',
        }}>

          {/* ── Page title + breadcrumb ── */}
          <div style={{ marginBottom: '16px' }}>
            <Text style={{ fontSize: '12px', color: '#8c8c8c' }}>
              Governance and Control&nbsp;/&nbsp;
            </Text>
            <Text style={{ fontSize: '12px', color: '#162349', fontWeight: 600 }}>
              HCC Pipeline Tracing
            </Text>
          </div>

          {/* ── Filter bar ── */}
          <div style={{
            background:   'white',
            padding:      '12px 16px',
            borderRadius: '6px',
            boxShadow:    '0 2px 8px rgba(0,0,0,0.1)',
            marginBottom: '16px',
            display:      'flex',
            alignItems:   'center',
            gap:          '12px',
          }}>
            <Select
              value={windowDays}
              onChange={setWindowDays}
              style={{ width: 160 }}
            >
              {WINDOW_OPTIONS.map(o => (
                <Option key={o.value} value={o.value}>{o.label}</Option>
              ))}
            </Select>

            <Button
              type="primary"
              onClick={() => loadBatches(windowDays)}
              loading={batchLoading}
              style={{ background: '#92A372', borderColor: '#92A372' }}
            >
              Apply
            </Button>

            <Button
              icon={<ReloadOutlined />}
              onClick={() => {
                setExpandedCharts({});
                loadBatches(windowDays);
              }}
              loading={batchLoading}
            >
              Refresh
            </Button>

            {batches.length > 0 && (
              <Text style={{ color: '#8c8c8c', fontSize: '13px', marginLeft: '8px' }}>
                {batches.length} batch run{batches.length !== 1 ? 's' : ''} found
              </Text>
            )}
          </div>

          {/* ── Batch table with expandable chart rows ── */}
          <div style={{
            background:   'white',
            borderRadius: '6px',
            boxShadow:    '0 2px 8px rgba(0,0,0,0.1)',
          }}>
            <Table
              columns={batchColumns}
              dataSource={batches}
              rowKey="trace_id"
              loading={batchLoading}
              size="small"
              pagination={{
                pageSize:        20,
                showSizeChanger: true,
                showTotal:       (total) => `${total} batch runs`,
                pageSizeOptions: ['10', '20', '50'],
              }}
              scroll={{ x: 1300 }}
              expandable={{
                expandedRowRender,
                onExpand: handleBatchExpand,
                // Re-expand cached rows without re-fetching
                rowExpandable: () => true,
              }}
              rowClassName={(record) =>
                record.status === 'FAILED' || record.status === 'failed'
                  ? 'hcc-trace-row-failed'
                  : ''
              }
            />
          </div>

          {/* ── Subtle row style for failed batches ── */}
          <style>{`
            .hcc-trace-row-failed td {
              background-color: #fff1f0 !important;
            }
            .hcc-trace-row-failed:hover td {
              background-color: #ffccc7 !important;
            }
          `}</style>

        </Content>
      </Layout>

      {/* ── Trace timeline modal
           ObservatoryService.baseUrl already targets the HCC observatory at
           port 9038, which now has /api/observatory/traces/.../spans and
           /api/observatory/spans/.../detail endpoints. No serviceUrl needed. ── */}
      <TraceTimelineModal
        visible={modalVisible}
        trace={selectedTrace}
        onClose={() => {
          setModalVisible(false);
          setSelectedTrace(null);
        }}
      />
    </Layout>
  );
};

export default HCCTracingPage;